import { SUPPORTED_LOCALES, type SupportedLocaleCode } from './index';

/**
 * Decides what to do when a signed-in user's saved language (from their
 * account) meets the language this device is currently showing.
 *
 * Kept as a pure function because the rules are fiddly and easy to get subtly
 * wrong in an effect — see reconcileLocale.test.ts.
 */

export interface ReconcileInput {
  /** `preferredLocale` from the account, or null if never chosen. */
  saved: string | null;
  /** The locale the UI is showing right now. */
  local: SupportedLocaleCode;
  /** False on the first pass for a user; true for later local changes. */
  reconciled: boolean;
}

export interface ReconcileAction {
  /** Switch the UI to this locale. */
  apply?: SupportedLocaleCode;
  /** Write this to the account. */
  save?: SupportedLocaleCode;
  /**
   * What the server is now believed to hold, so a later local change can tell
   * whether it needs pushing up. Undefined means "leave the belief unchanged".
   */
  serverNowHolds?: string | null;
}

export function isSupportedLocale(value: string): value is SupportedLocaleCode {
  return SUPPORTED_LOCALES.some((l) => l.code === value);
}

export function reconcileLocale({ saved, local, reconciled }: ReconcileInput): ReconcileAction {
  if (!reconciled) {
    // First look at this user's account.
    if (saved) {
      // The account wins — that's the whole point of storing it there, so the
      // choice follows the user to a new device or a cleared browser.
      //
      // A saved locale this build doesn't ship (e.g. set from a newer build) is
      // remembered but not applied: better to keep showing a language we have
      // than to blank the UI, and we must not overwrite their real choice.
      return {
        apply: isSupportedLocale(saved) && saved !== local ? saved : undefined,
        serverNowHolds: saved,
      };
    }
    // Never chosen: adopt whatever this device already detected, so the account
    // starts out matching what the user is actually looking at.
    return { save: local, serverNowHolds: local };
  }

  // Later passes: the user changed language in the UI, so push it up. Compared
  // against `saved` (our belief about the account) rather than re-reading /me,
  // which avoids a save→refetch→save loop.
  if (saved !== local) {
    return { save: local, serverNowHolds: local };
  }
  return {};
}
